import {
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    Input,
    Select,
    useToast,
    Tooltip,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    useDisclosure,
    Link,
    chakra,
} from '@chakra-ui/react';
import { MdVisibility } from 'react-icons/md';
import { FaCheckCircle } from 'react-icons/fa';
import moment from 'moment';
import React, { useState, useEffect, useRef } from 'react';
import CommunicationApi from 'api/communication';
import { usePermissions } from 'contexts/PermissionContext';
import { Modules, CommunicationChannel } from 'config/constant';
import NoPermissionsMessage from 'components/nopermission/NoPermissions';
import TableWrapper from 'components/tableWrapper/tableWrapper';
import PageError from 'components/nopermission/PageError';

function truncateText(text, maxLength) {
    if (text && text.length > maxLength) {
        return text.substring(0, maxLength) + '...';
    }
    return text;
}

function stripHtml(html) {
    if (!html || typeof html !== 'string') return '';
    return html.replace(/<[^>]*>?/gm, '');
}

function getContentPreview(obj) {
    const details = obj.details;
    const content = obj.content;

    const getParsed = (f) => {
        if (!f) return null;
        try {
            return typeof f === 'string' ? JSON.parse(f) : f;
        } catch (e) { return null; }
    };

    const parsed = getParsed(details) || getParsed(content);

    if (parsed && typeof parsed === 'object') {
        if (obj.channel === 'MAIL') {
            const subject = parsed.subject || '';
            const to = parsed.to || '';
            if (subject || to) {
                return `${subject ? 'Sub: ' + subject : ''} ${to ? (subject ? '| ' : '') + 'To: ' + to : ''}`;
            }
        } else if (obj.channel === 'SMS') {
            let smsText = parsed.body || parsed.message || '';
            if (typeof smsText === 'string' && smsText.includes('text=')) {
                const textParts = smsText.split(/[?&]text=/);
                if (textParts.length > 1) {
                    try {
                        const rawText = textParts[1].split('&')[0];
                        smsText = decodeURIComponent(rawText.replace(/\+/g, ' '));
                    } catch (e) {
                        smsText = textParts[1].split('&')[0].replace(/\+/g, ' ');
                    }
                }
            }
            if (smsText) return truncateText(smsText, 50);
        } else if (obj.channel === 'PUSH_NOTIFICATION') {
            const notificationData = parsed.data || parsed;
            if (notificationData.title) return notificationData.title;
        }
    }

    return truncateText(stripHtml(content || (details && typeof details === 'string' ? details : '') || ''), 50);
}

function getTooltipContent(obj) {
    const details = obj.details;
    const content = obj.content;

    const getParsed = (f) => {
        if (!f) return null;
        try {
            return typeof f === 'string' ? JSON.parse(f) : f;
        } catch (e) { return null; }
    };

    const parsed = getParsed(details) || getParsed(content);

    if (parsed && typeof parsed === 'object') {
        if (obj.channel === 'MAIL') {
            const body = parsed.body || content || '';
            const subject = parsed.subject ? `Subject: ${parsed.subject}\n` : '';
            return (subject + stripHtml(body)).trim();
        } else if (obj.channel === 'SMS') {
            let smsText = parsed.body || parsed.message || content || '';
            if (typeof smsText === 'string' && smsText.includes('text=')) {
                const textParts = smsText.split(/[?&]text=/);
                if (textParts.length > 1) {
                    try {
                        const rawText = textParts[1].split('&')[0];
                        smsText = decodeURIComponent(rawText.replace(/\+/g, ' '));
                    } catch (e) {
                        smsText = textParts[1].split('&')[0].replace(/\+/g, ' ');
                    }
                }
            }
            return stripHtml(smsText).trim();
        } else if (obj.channel === 'PUSH_NOTIFICATION') {
            const notificationData = parsed.data || parsed;
            const title = notificationData.title ? `Title: ${notificationData.title}\n` : '';
            const body = notificationData.body ? `Body: ${notificationData.body}` : '';
            return (title + body).trim();
        }
    }

    return stripHtml(content || (details && typeof details === 'string' ? details : '') || '').trim();
}

export default function CommunicationTable(props) {
    const { withId } = props;
    function IdFromUrl(url) {
        const parts = url.split('/');
        const id = parts[parts.length - 1];
        return id;
    }
    const customerId = React.useMemo(() => withId ? IdFromUrl(window.location.href) : null, [withId]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [listTotal, setListTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [channelFilter, setChannelFilter] = useState('PUSH_NOTIFICATION');
    const { getPermissionsForModule } = usePermissions();
    const [permissions, setPermissions] = useState(null);
    const [fetchListError, setFetchListError] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedContent, setSelectedContent] = useState('');
    const [selectedChannel, setSelectedChannel] = useState('');

    const openContentModal = (obj) => {
        let displayContent = '';
        setSelectedChannel(obj.channel);

        const getParsed = (f) => {
            if (!f) return null;
            try {
                return typeof f === 'string' ? JSON.parse(f) : f;
            } catch (e) { return null; }
        };

        const parsed = getParsed(obj.details) || getParsed(obj.content);

        if (parsed && typeof parsed === 'object') {
            if (obj.channel === 'PUSH_NOTIFICATION') {
                const notificationData = parsed.data || parsed;
                if (notificationData.title || notificationData.body) {
                    displayContent = `<strong>Title:</strong> ${notificationData.title || 'N/A'}<br/><br/><strong>Body:</strong> ${notificationData.body || 'N/A'}`;
                }
            } else if (obj.channel === 'MAIL') {
                if (parsed.to || parsed.subject || parsed.body) {
                    displayContent = `<strong>To:</strong> ${parsed.to || 'N/A'}<br/><strong>Subject:</strong> ${parsed.subject || 'N/A'}<br/><br/><div style="border-top: 1px solid #ddd; padding-top: 10px;">${parsed.body || obj.content || ''}</div>`;
                }
            } else if (obj.channel === 'SMS') {
                let smsText = parsed.body || parsed.message || '';
                if (typeof smsText === 'string' && smsText.includes('text=')) {
                    const textParts = smsText.split(/[?&]text=/);
                    if (textParts.length > 1) {
                        try {
                            const rawText = textParts[1].split('&')[0];
                            smsText = decodeURIComponent(rawText.replace(/\+/g, ' '));
                        } catch (e) {
                            smsText = textParts[1].split('&')[0].replace(/\+/g, ' ');
                        }
                    }
                }
                displayContent = `<strong>To:</strong> ${parsed.to || 'N/A'}<br/><strong>Type:</strong> ${parsed.otp_type || 'N/A'}<br/><br/><strong>Message:</strong><br/>${smsText || obj.content || 'N/A'}`;
            }
        }

        if (!displayContent) {
            displayContent = typeof obj.content === 'string' ? obj.content : JSON.stringify(obj.content || obj.details || 'N/A', null, 2);
        }

        setSelectedContent(displayContent);
        onOpen();
    };

    const debounceTimeout = useRef(null);
    const handleInputChange = (value) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            setSearchTerm(value);
        }, 800);
    };

    useEffect(() => {
        const permissions = getPermissionsForModule([Modules.CustomerTicket]);
        setPermissions(permissions);
    }, []);

    const fetchList = async () => {
        try {
            setLoading(true);
            let response = await CommunicationApi.List({
                searchTerm,
                channel: channelFilter,
                currentPage,
                itemsPerPage,
                userId: customerId,
            });
            response = response.data;
            setLoading(false);
            if (response.status_code === 200) {
                setFetchListError(false);
                setList(response?.data?.list);
                setListTotal(response?.data?.total);
            } else {
                setFetchListError(true);
                setList([]);
                setListTotal(0);
            }
        } catch (err) {
            setLoading(false);
            setFetchListError(true);
            console.log(err);
        }
    };

    useEffect(() => {
        if (permissions?.read) {
            fetchList();
        }
    }, [currentPage, searchTerm, channelFilter, permissions]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, channelFilter]);

    const user = JSON.parse(sessionStorage.getItem('user'));
    const isAllowedUser = user?.user_type === 'SUPER_ADMIN' || user?.user_type === 'ADMIN';

    const GoToCustomerUser = (id) => {
        const url = `/admin/customeruser/${id}`;
        window.open(url, '_blank');
    };

    if (!permissions?.read || !isAllowedUser) {
        return <NoPermissionsMessage module={Modules.UserCommunication} />;
    }

    return fetchListError ? (
        <PageError />
    ) : (
        <Flex height='100%' mt={withId ? '0px' : '8rem'} direction='column'>
            <Flex direction={{ sm: 'column', md: 'row' }} px={withId ? 0 : 5} pt={withId ? 0 : 5} mb='0px' width='100%' gap={10} align="center">
                <Flex direction={{ sm: 'column', lg: 'row' }} gap={10} align="center">
                    {!withId && (
                        <Input
                            variant='auth'
                            minWidth='300px'
                            type='text'
                            placeholder='Search by Name, Phone or Email'
                            onChange={(e) => handleInputChange(e.target.value)}
                        />
                    )}

                    <Flex align="center" gap={2}>
                        {withId && <Text fontSize='sm' fontWeight='700' color={textColor}>Channel:</Text>}
                        <Select
                            minW='150px'
                            id='channel-filter'
                            value={channelFilter}
                            variant='auth'
                            onChange={(e) => setChannelFilter(e.target.value)}>
                            {CommunicationChannel.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                    </Flex>
                </Flex>
            </Flex>

            <TableWrapper
                list={list}
                loading={loading}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                listTotal={listTotal}
                itemsPerPage={itemsPerPage}>
                <Table variant='striped' color='gray.500' mb='24px'>
                    <Thead>
                        <Tr>
                            {!withId && (
                                <Th pe='10px' borderColor={borderColor}>
                                    <Text fontSize={{ sm: '10px', lg: '12px' }} color='gray.400'>
                                        Customer Name
                                    </Text>
                                </Th>
                            )}
                            {!withId && (
                                <Th pe='10px' borderColor={borderColor}>
                                    <Text fontSize={{ sm: '10px', lg: '12px' }} color='gray.400'>
                                        Phone / Email
                                    </Text>
                                </Th>
                            )}
                            <Th pe='10px' borderColor={borderColor}>
                                <Text fontSize={{ sm: '10px', lg: '12px' }} color='gray.400'>
                                    Channel
                                </Text>
                            </Th>
                            <Th pe='10px' borderColor={borderColor}>
                                <Text fontSize={{ sm: '10px', lg: '12px' }} color='gray.400'>
                                    Content
                                </Text>
                            </Th>
                            <Th pe='10px' borderColor={borderColor}>
                                <Text fontSize={{ sm: '10px', lg: '12px' }} color='gray.400'>
                                    Status
                                </Text>
                            </Th>
                            <Th pe='10px' borderColor={borderColor}>
                                <Text fontSize={{ sm: '10px', lg: '12px' }} color='gray.400'>
                                    Sent At
                                </Text>
                            </Th>
                            <Th pe='10px' borderColor={borderColor}>
                                <Text fontSize={{ sm: '10px', lg: '12px' }} color='gray.400'>
                                    Action
                                </Text>
                            </Th>
                        </Tr>
                    </Thead>

                    <Tbody>
                        {list?.map((obj) => (
                            <Tr key={obj.id}>
                                {!withId && (
                                    <Td>
                                        <Text
                                            fontSize='sm'
                                            fontWeight='700'
                                            color="brand.500"
                                            cursor="pointer"
                                            _hover={{ textDecoration: 'underline' }}
                                            onClick={() => GoToCustomerUser(obj.user_id)}>
                                            {obj.customer_name}
                                        </Text>
                                    </Td>
                                )}
                                {!withId && (
                                    <Td>
                                        <Text color={textColor} fontSize='sm' fontWeight='400'>
                                            {obj.phone_number} <br />
                                            {obj.email}
                                        </Text>
                                    </Td>
                                )}
                                <Td>
                                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                                        {obj.channel}
                                    </Text>
                                </Td>
                                <Td maxW='300px'>
                                    <Tooltip label={getTooltipContent(obj)}>
                                        <Text color={textColor} fontSize='sm' fontWeight='400' noOfLines={2}>
                                            {getContentPreview(obj)}
                                        </Text>
                                    </Tooltip>
                                </Td>
                                <Td>
                                    <Flex direction="column">
                                        <Text color={textColor} fontSize='sm' fontWeight='700'>
                                            {obj.status ? 'Success' : 'Failed'}
                                        </Text>
                                        {obj.channel === 'PUSH_NOTIFICATION' && obj.is_seen && (
                                            <Tooltip
                                                label={obj.seen_date ? `Seen At: ${moment(obj.seen_date).format('DD MMM YY, HH:mm')}` : 'Seen'}
                                            >
                                                <chakra.div mt={1}>
                                                    <FaCheckCircle
                                                        color='green'
                                                        style={{ fontSize: '20px' }}
                                                    />
                                                </chakra.div>
                                            </Tooltip>
                                        )}
                                    </Flex>
                                </Td>
                                <Td>
                                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                                        {moment(obj.created_at).format('DD MMM YY, HH:mm')}
                                    </Text>
                                </Td>
                                <Td>
                                    <Tooltip label="View Content">
                                        <IconButton
                                            icon={<MdVisibility />}
                                            colorScheme="brand"
                                            variant="ghost"
                                            onClick={() => openContentModal(obj)}
                                        />
                                    </Tooltip>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </TableWrapper>

            <Modal isOpen={isOpen} onClose={onClose} size="3xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Communication Details - {selectedChannel}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <div
                            style={{
                                border: '1px solid #e2e8f0',
                                padding: '20px',
                                borderRadius: '8px',
                                backgroundColor: '#f7fafc',
                                maxHeight: '60vh',
                                overflowY: 'auto'
                            }}
                            dangerouslySetInnerHTML={{ __html: selectedContent }}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="brand" mr={3} onClick={onClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Flex>
    );
}
